import { useEffect, useRef, useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

/**
 * One-line text that expands to its full content on click, with a smooth
 * height animation (MUI Collapse). A chevron appears only when the text is
 * actually long enough to be truncated. Used for ticket descriptions in the
 * Timesheet and Live Activity screens.
 *
 * Props:
 *   text            — the string to show
 *   sx              — typography styles (fontSize, color, …)
 *   collapsedHeight — px height shown when collapsed (≈ one line)
 *   iconColor       — chevron colour
 */
const ExpandableText = ({ text, sx = {}, collapsedHeight = 22, iconColor = "#9AA2B1" }) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Detect truncation while collapsed: a single nowrap line whose content is
  // wider than its box is "long". (Skip while expanded — it wraps by design.)
  useEffect(() => {
    if (expanded) return;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, expanded]);

  const toggle = (e) => {
    e.stopPropagation();
    if (overflowing) setExpanded((v) => !v);
  };

  return (
    <Box
      onClick={toggle}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.5,
        minWidth: 0,
        cursor: overflowing ? "pointer" : "default",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Collapse in={expanded} collapsedSize={collapsedHeight}>
          <Typography
            ref={ref}
            sx={{
              ...sx,
              whiteSpace: expanded ? "normal" : "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {text}
          </Typography>
        </Collapse>
      </Box>
      {overflowing && (
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 16,
            color: iconColor,
            flexShrink: 0,
            mt: "1px",
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.25s ease",
          }}
        />
      )}
    </Box>
  );
};

export default ExpandableText;
